import type { RefObject } from 'react';
import gsap from 'gsap';
import type { HeadingRef } from '../../atoms/heading/Heading.data';
import { findTransitionController } from '../../../util/core-transition-component';
import MorphSVGPlugin from 'gsap/dist/MorphSVGPlugin';

gsap.registerPlugin(MorphSVGPlugin);

interface AccordionItemRefs {
  elementRef?: RefObject<HTMLElement | null>;
  backgroundRef?: RefObject<HTMLDivElement | null>;
  iconRef?: RefObject<HTMLDivElement | null>;
  headingRef?: RefObject<HeadingRef | null>;
  lineRef?: RefObject<HTMLSpanElement | null>;
}

export function setupTransitionInTimeline(
  timeline: gsap.core.Timeline,
  { headingRef, lineRef, iconRef }: AccordionItemRefs,
): void {
  const title = findTransitionController(headingRef);

  if (lineRef?.current) {
    timeline.fromTo(
      lineRef.current,
      { scaleX: 0, transformOrigin: 'left center' },
      { duration: 0.5, ease: 'Quad.easeInOut', scaleX: 1 },
    );
  }

  if (iconRef?.current) {
    timeline.fromTo(
      iconRef.current,
      { opacity: 0, scale: 0.4 },
      {
        scale: 1,
        opacity: 1,
        ease: 'Quad.easeInOut',
        clearProps: 'scale, opacity',
        duration: 0.4,
      },
      0.3,
    );
  }

  if (title) {
    timeline.add(title.getTimeline('in') as gsap.core.Timeline, 0);
  }
}

export function toggle(
  listRef: RefObject<HTMLUListElement | null>,
  isOpen: boolean,
  callback: () => void,
  immediate: boolean = false,
): void {
  gsap.to(listRef.current, {
    height: isOpen ? (listRef.current as HTMLUListElement).scrollHeight : 0,
    duration: immediate ? 0 : 0.6,
    ease: 'Quad.easeInOut',
    clearProps: isOpen ? 'height' : '',
    onComplete: () => {
      if (callback) {
        callback();
      }
    },
  });
}

export function toggleIconState(
  refs: {
    iconRef: RefObject<HTMLDivElement | null>;
    iconPlusRef: RefObject<HTMLDivElement | null>;
    iconMinusRef: RefObject<HTMLDivElement | null>;
  },
  isOpen: boolean,
): void {
  const timeline = gsap.timeline();

  if (refs.iconRef) {
    timeline.to(refs.iconRef.current, {
      rotation: isOpen ? 180 : 0,
      duration: 0.4,
      ease: 'Power2.easeOut',
    });

    if (refs.iconRef.current?.firstElementChild?.firstElementChild) {
      // @ts-ignore
      timeline.to(
        refs.iconRef.current.firstElementChild.firstElementChild,
        {
          morphSVG: isOpen
            ? refs.iconMinusRef.current?.firstElementChild?.firstElementChild
            : refs.iconPlusRef.current?.firstElementChild?.firstElementChild,
          duration: 0.05,
          ease: 'none',
        },
        '<',
      );
    }
  }
}

export function setupMouseEnterTimeline(
  timeline: gsap.core.Timeline,
  { backgroundRef, iconRef }: AccordionItemRefs,
): void {
  if (backgroundRef?.current) {
    timeline.fromTo(
      backgroundRef.current,
      { scaleY: 0 },
      { duration: 0.4, ease: 'Expo.easeOut', scaleY: 1 },
    );
  }

  if (iconRef?.current) {
    timeline.fromTo(
      iconRef.current,
      { rotate: 0, scale: 1 },
      { duration: 0.4, ease: 'Power2.easeOut', rotate: 90, scale: 1 },
      0,
    );
  }
}

export function setupMouseLeaveTimeline(
  timeline: gsap.core.Timeline,
  { backgroundRef, iconRef }: AccordionItemRefs,
): void {
  if (backgroundRef?.current) {
    timeline.to(backgroundRef.current, { duration: 0.4, ease: 'Expo.easeOut', scaleY: 0 });
  }

  if (iconRef?.current) {
    timeline.to(
      iconRef.current,
      { duration: 0.4, ease: 'Power2.easeOut', rotate: 0 },

      0,
    );
  }
}
